import React, { useState, useMemo } from 'react';
import { Card, Typography, Box, Stack, Chip, CircularProgress, Alert, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import thunderbolt from '@iconify/icons-ant-design/thunderbolt';
import rise from '@iconify/icons-ant-design/rise';
import poweroff from '@iconify/icons-ant-design/poweroff';
import environment from '@iconify/icons-ant-design/environment';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
import useEnergyConsumption from 'src/hooks/useEnergyConsumption.hook';
import { environmentalImpactFromEnergy } from 'src/utils/carbonFootprint';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  border: `1px solid ${theme.palette.grey[200]}`,
  textAlign: 'center'
}));

// Helper function to format API data for charts
const formatEnergyData = (apiData, period) => {
  if (!apiData?.data?.trends?.dataPoints) {
    return {
      series: [],
      options: {}
    };
  }

  const { dataPoints } = apiData.data.trends;
  
  // Handle case where dataPoints might be empty or invalid
  if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
    return {
      series: [],
      options: {}
    };
  }
  
  const labels = dataPoints.map(point => {
    try {
      return new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (error) {
      console.warn('Invalid date format:', point.date);
      return 'Invalid Date';
    }
  });
  
  const totalEnergyData = dataPoints.map(point => point.totalEnergy || 0);
  const gridEnergyData = dataPoints.map(point => point.gridEnergy || 0);

  const datasets = [
    {
      name: 'Total Energy',
      data: totalEnergyData,
      color: '#3182CE',
    },
    {
      name: 'Grid Energy',
      data: gridEnergyData,
      color: '#ED8936',
    },
  ];

  return {
    series: datasets,
    options: {
      chart: {
        type: 'area',
        height: 350,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        toolbar: {
          show: false,
        },
        background: 'transparent',
        zoom: { enabled: false },
        pan: { enabled: false },
        selection: { enabled: false },
        brush: { enabled: false },
        animations: { enabled: false }
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.5,
          gradientToColors: ['#3182CE20', '#ED893620'],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.2,
        }
      },
      markers: {
        size: 4,
        strokeWidth: 2,
        hover: {
          size: 6,
        },
      },
      xaxis: {
        categories: labels,
        title: {
          text: 'Date',
          style: {
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#718096'
          },
        },
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          }
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        title: {
          text: 'Energy (kWh)',
          style: {
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#718096'
          },
        },
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 400,
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            colors: '#4A5568'
          },
          formatter: (value) => `${value.toLocaleString()} kWh`
        }
      },
      grid: {
        show: true,
        borderColor: '#E2E8F0',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        markers: {
          width: 8,
          height: 8,
          radius: 4
        }
      },
      tooltip: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        },
        y: {
          formatter: (value) => `${value.toLocaleString()} kWh`
        },
        followCursor: true,
        offsetY: -10,
        offsetX: 0,
        fixed: {
          enabled: false
        },
        position: 'top',
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
          const value = data ? `${data.toLocaleString()} kWh` : '0 kWh';
          const seriesName = w.globals.initialSeries[seriesIndex].name || 'Energy';
          return `
            <div style="
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 12px;
              font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              max-width: 200px;
              word-wrap: break-word;
              z-index: 1000;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${seriesName}</div>
              <div>${value}</div>
            </div>
          `;
        }
      },
      dataLabels: {
        enabled: false
      }
    }
  };
};

const EnergyConsumptionTrends = ({ timeRange = 'Last7Days', carbonConstants }) => {
  // Convert dashboard time period to chart format
  const getChartPeriod = (dashboardTimeRange) => {
    switch (dashboardTimeRange) {
      case 'Today':
        return '1d';
      case 'Last7Days':
        return '7d';
      case 'Last30Days':
        return '30d';
      case 'Last90Days':
        return '90d';
      default:
        return '7d';
    }
  };

  const period = getChartPeriod(timeRange);

  const { data, isLoading, error, refresh } = useEnergyConsumption(period);

  const energyData = useMemo(() => {
    return formatEnergyData(data, period);
  }, [data, period]);

  // Extract metrics from API data
  const summary = data?.data?.summary || {};
  const serverImpact = data?.data?.environmentalImpact;

  const currentEnergy = summary.totalEnergy || 0;
  const growthRate = summary.growthRate || 0;
  const totalEnergy = summary.totalEnergy || 0;
  const avgDailyEnergy = summary.averageDailyEnergy || 0;

  // Prefer the backend's environmentalImpact (source of truth) and fall back to
  // the shared carbon utility if it's missing. The backend now uses the same
  // methodology as the util (src/utils/carbonFootprint.js), so both paths agree.
  const calculatedEnvironmentalImpact = useMemo(
    () =>
      serverImpact && serverImpact.totalCO2Emissions != null
        ? serverImpact
        : environmentalImpactFromEnergy(totalEnergy, carbonConstants),
    [serverImpact, totalEnergy, carbonConstants]
  );

  // Loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }

  // Error state
  if (error) {
    return (
      <StyledCard>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
        >
          Failed to load energy consumption data. Please try again.
        </Alert>
      </StyledCard>
    );
  }

  // No data state
  if (!data || !data.data) {
    return (
      <StyledCard>
        <Alert severity="info">
          No energy consumption data available for the selected period.
        </Alert>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Energy Consumption Trends
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Track energy usage and environmental impact
        </Typography>

        {/* Key Metrics */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <MetricCard sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#3182CE' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {currentEnergy.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total Energy (kWh)
            </Typography>
          </MetricCard>

          <MetricCard sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <Icon icon={poweroff} width={20} height={20} style={{ marginRight: 8, color: '#805AD5' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {avgDailyEnergy.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Avg Daily
            </Typography>
          </MetricCard>
        </Stack>

        {/* Environmental Impact */}
        <Box sx={{ mb: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 2, border: '1px solid', borderColor: 'success.light' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Icon icon={environment} width={24} height={24} style={{ color: '#38A169' }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                Environmental Impact
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                Total CO₂ emissions: {calculatedEnvironmentalImpact.totalCO2Emissions.toLocaleString()} kg
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Baseline (grid only): {calculatedEnvironmentalImpact.baselineCO2Emissions.toLocaleString()} kg
              </Typography>
            </Box>
          </Stack>
          
          {/* Additional Environmental Metrics */}
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                {calculatedEnvironmentalImpact.carbonFootprintReduction}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Carbon Reduction
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {calculatedEnvironmentalImpact.sustainabilityScore}/10
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Sustainability Score
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {calculatedEnvironmentalImpact.treesEquivalent}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Trees Equivalent
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {calculatedEnvironmentalImpact.carbonOffset.toLocaleString()} kg
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                CO₂ Offset
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1 }}>
        <BaseChart
          series={energyData.series}
          options={energyData.options}
          type="area"
          height={350}
        />
      </Box>
    </StyledCard>
  );
};

export default EnergyConsumptionTrends;
